import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { X, Save } from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetClose
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { FormInput, FormSelect, FormComboboxStatic } from "@/components/forms";
import type { ComboboxOption } from "@/components/forms/form-combobox-static";
import {
  FormMultipleComboboxAsync,
  MultipleComboboxItem
} from "@/components/forms/form-multiple-combobox-async";
import { WorkspaceList } from "@/hooks/services/pps/handlers/workspace-api";
import { ShopList, ShopItem } from "@/hooks/services/pps/handlers/shop-api";
import {
  useGetData,
  useInvalidateQueries
} from "@/hooks/services/use-query-hooks";
import { toastManager } from "@/lib/toast-manager";
import { extractApiMessages } from "@/lib/parse-api-errors";
import { useWorkSpaceStore } from "@/stores/store";
import { useMachine, useMachineOperations } from "@/hooks/use-machines";
import { technicianMultipleApiConfig } from "./search-combobox-configs";

const formSchema = z.object({
  machineId: z
    .string()
    .length(3, "Machine ID must be exactly 3 digits")
    .regex(/^\d{3}$/, "Machine ID must contain only numbers"),
  name: z.string().min(1, "Machine name is required"),
  shopId: z.number().min(1, "Please select a shop"),
  workspaceId: z.number().min(1, "Workspace ID is required"),
  status: z.number().min(1, "Please select a status"),
  technicianIds: z.array(z.number()).optional().default([])
});

type FormData = z.infer<typeof formSchema>;

const statusOptions = [
  { value: 1, label: "Available" },
  { value: 2, label: "Maintenance" },
  { value: 3, label: "Unavailable" }
];

interface EditMachineSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  machineId: string;
  onSuccess?: () => void;
}

export function EditMachineSheet({
  open,
  onOpenChange,
  machineId,
  onSuccess
}: EditMachineSheetProps) {
  const { workspace } = useWorkSpaceStore();

  const { machine: machineDetail, isLoading: isLoadingMachine } = useMachine(
    machineId,
    { enabled: open && Boolean(machineId) }
  );
  const { updateMachine, isUpdating } = useMachineOperations();
  const invalidate = useInvalidateQueries();

  // Fetch data
  const { data: workspaceData } = useGetData(
    WorkspaceList({ page: 1, pageSize: 100 }),
    { enabled: open }
  );

  const { data: shopData } = useGetData(ShopList({ page: 1, pageSize: 100 }), {
    enabled: open
  });

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      machineId: "",
      name: "",
      shopId: 0,
      workspaceId: workspace?.id || 0,
      status: 1,
      technicianIds: []
    }
  });

  const selectedShopId = form.watch("shopId");

  // Reset form and state when sheet closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  // Transform to options
  const workspaceOptions =
    workspaceData?.data?.map((ws) => ({
      value: ws.id,
      label: ws.workspaceName
    })) || [];

  const shopOptions =
    shopData?.data?.map((shop: ShopItem) => ({
      value: shop.id.toString(),
      label: shop.shopName
    })) || [];

  // Transform technicians for initialItems
  const initialTechnicianItems: MultipleComboboxItem[] = useMemo(() => {
    if (!machineDetail) return [];
    const machine: any = machineDetail;
    return (
      machine.technicians?.map((t: any) => ({
        id: t.id,
        label: t.name || t.displayName || `Technician ${t.id}`,
        value: t.name || t.displayName || "",
        subtitle: t.email || t.employeeId || undefined,
        raw: t
      })) || []
    );
  }, [machineDetail]);

  // Load machine data into form
  useEffect(() => {
    if (machineDetail && open) {
      const machine: any = machineDetail;

      form.setValue("machineId", machine.machineIdFormatted || "");
      form.setValue("name", machine.name || "");
      form.setValue("shopId", machine.shopId || 0);
      form.setValue("workspaceId", machine.workspaceId || 0);
      form.setValue("status", machine.status?.id || 1);
      form.setValue(
        "technicianIds",
        machine.technicians?.map((t: any) => t.id) || []
      );
    }
  }, [machineDetail, open, form]);

  const handleSubmit = (data: FormData) => {
    updateMachine({
      machineId: Number(machineId),
      body: data
    })
      .then(() => {
        form.reset();
        onOpenChange(false);
        toastManager.showSuccess("Machine updated successfully");
        invalidate([
          ["MachineListGroupedByShop"],
          ["MachineList"],
          ["MachineDetails"],
          ["TechnicianList"],
          ["TechnicianGroupedByShop"]
        ]);
        onSuccess?.();
      })
      .catch((error: unknown) => {
        toastManager.showError(
          extractApiMessages(
            error,
            "Failed to update machine. Please try again."
          )
        );
      });
  };

  const handleShopSelect = (option: ComboboxOption | null) => {
    if (option) {
      form.setValue("technicianIds", []);
      form.setValue("shopId", Number(option.value));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="aero:sm:max-w-[600px] aero:p-0 aero:gap-0 aero:flex aero:flex-col"
      >
        {/* Custom close button - hide default */}
        <style>{`
          [data-slot="sheet-content"] > button[class*="absolute"][class*="right-4"] {
            display: none;
          }
        `}</style>

        {/* Header */}
        <SheetHeader className="aero:p-6 aero:pb-0 aero:gap-3 aero:border-b aero:border-neutral-200">
          <div className="aero:flex aero:items-center aero:justify-between aero:h-12 aero:gap-3">
            <SheetTitle className="aero:font-inter aero:font-bold aero:text-xl aero:leading-9 aero:text-foreground">
              Edit Machine
            </SheetTitle>
            <SheetClose asChild>
              <button className="aero:w-6 aero:h-6 aero:flex aero:items-center aero:justify-center aero:rounded aero:hover:bg-neutral-100">
                <X className="aero:w-5 aero:h-5 aero:text-neutral-500" />
              </button>
            </SheetClose>
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="aero:flex-1 aero:overflow-y-auto aero:px-6 aero:py-5">
          {isLoadingMachine ? (
            <div className="aero:flex aero:items-center aero:justify-center aero:h-full">
              <div className="aero:text-center">
                <div className="aero:animate-spin aero:rounded-full aero:h-12 aero:w-12 aero:border-b-2 aero:border-brand-primary-600 aero:mx-auto"></div>
                <p className="aero:mt-4 aero:text-sm aero:text-neutral-600">
                  Loading machine details...
                </p>
              </div>
            </div>
          ) : (
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(handleSubmit)}
                className="aero:flex aero:flex-col aero:gap-5"
              >
                {/* Section: Basic Information */}
                <div className="aero:flex aero:flex-col aero:gap-5">
                  <div className="aero:flex aero:items-center aero:gap-2">
                    <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                    <h3 className="aero:font-inter aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                      Basic Information
                    </h3>
                  </div>

                  <div className="aero:grid aero:grid-cols-2 aero:gap-4">
                    <FormInput
                      control={form.control}
                      name="machineId"
                      label="Machine ID"
                      placeholder="e.g., 001"
                      required
                      type="text"
                      maxLength={3}
                      numbersOnly
                      inputClassName="aero:font-mono"
                      disabled
                    />

                    <FormInput
                      control={form.control}
                      name="name"
                      label="Machine Name"
                      placeholder="e.g., CNC Mill #1"
                      required
                      disabled={isUpdating}
                    />
                  </div>

                  <div className="aero:grid aero:grid-cols-2 aero:gap-4">
                    <FormComboboxStatic
                      control={form.control}
                      name="shopId"
                      label="Shop"
                      placeholder="Select a shop..."
                      options={shopOptions}
                      onSelectItem={handleShopSelect}
                      required
                    />

                    <FormSelect
                      control={form.control}
                      name="workspaceId"
                      label="Workspace"
                      placeholder="Select workspace"
                      triggerClassName="aero:w-full"
                      options={workspaceOptions}
                      required
                      disabled={isUpdating}
                    />
                  </div>

                  <FormSelect
                    control={form.control}
                    name="status"
                    label="Status"
                    placeholder="Select status"
                    options={statusOptions}
                    triggerClassName="aero:w-full"
                    required
                    disabled={isUpdating}
                  />
                </div>

                {/* Section: Technicians */}
                <div className="aero:flex aero:flex-col aero:gap-5">
                  <div className="aero:flex aero:items-center aero:gap-2">
                    <div className="aero:w-[3px] aero:h-7 aero:bg-brand-primary-600 aero:rounded-full" />
                    <h3 className="aero:font-inter aero:font-bold aero:text-lg aero:leading-7 aero:text-brand-primary-600">
                      Technicians
                    </h3>
                  </div>

                  <FormMultipleComboboxAsync
                    control={form.control}
                    name="technicianIds"
                    label="Technicians"
                    placeholder="Search and select technicians..."
                    searchPlaceholder="Type to search technicians..."
                    apiConfig={technicianMultipleApiConfig}
                    additionalParams={{
                      workspaceId: workspace?.id || null,
                      shopId: selectedShopId
                    }}
                    disabled={isUpdating}
                    debounceDelay={500}
                    minSearchLength={0}
                    initialItems={initialTechnicianItems}
                  />
                </div>
              </form>
            </Form>
          )}
        </div>

        {/* Footer */}
        <SheetFooter className="aero:p-6 aero:pt-4 aero:border-t aero:border-neutral-200 aero:flex aero:flex-row aero:justify-end aero:gap-3">
          <Button
            type="button"
            intent="neutral"
            design="outline"
            onClick={() => onOpenChange(false)}
            disabled={isUpdating}
            className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2"
          >
            <X className="aero:w-4 aero:h-4" />
            Cancel
          </Button>
          <Button
            type="submit"
            intent="primary"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={isUpdating || isLoadingMachine}
            className="aero:h-10 aero:gap-2 aero:rounded aero:px-3 aero:py-2"
          >
            {isUpdating ? (
              <>
                <span className="aero:animate-spin aero:w-4 aero:h-4 aero:border-2 aero:border-white aero:border-t-transparent aero:rounded-full" />
                Updating...
              </>
            ) : (
              <>
                <Save className="aero:w-4 aero:h-4" />
                Update Machine
              </>
            )}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
